import PropTypes from 'prop-types'
import React, {createContext, useCallback, useContext, useMemo, useRef, useEffect} from 'react'
import {productSetUrlBuilder, productUrlBuilder} from '../utils/url'
import {getDisplayPrice, isProductSet} from '../utils/product-utils'
import {useIntl} from 'react-intl'
import useMultiSite from '../hooks/use-multi-site'
import {useCurrentCustomer} from '../hooks/use-current-customer'
import {useMutation} from '@tanstack/react-query'
import {
    filterSetCombinationImages,
    getProductSetSelectionImageName
} from '../utils/image-groups-utils'
import {useProductDisplayImages} from '../hooks/use-product-display-images'
import {PRODUCT_TILE_IMAGE_TYPE} from '../constants'
import {trackAddToBabylistClick} from '../tracking/analytics'

const BabylistContext = createContext({enabled: false, apiLoader: async () => {}})

export const useBabylistAPILoader = () => {
    const ctx = useContext(BabylistContext)
    if (!ctx) throw new Error('useBabylistAPILoader must be used within a BabylistAPIProvider')
    return ctx
}
export const useBabylistEnabled = () => useBabylistAPILoader().enabled

export const BabylistAPIProvider = ({children, enabled = false, siteKey = 'stokke.com'}) => {
    const requestRef = useRef(null)

    const apiLoader = useCallback(() => {
        if (!enabled) return Promise.reject(new Error('BabylistAPI is not enabled'))
        if (!siteKey) return Promise.reject(new Error('BabylistAPI enabled without siteKey'))
        if (typeof window === 'undefined')
            return Promise.reject(new Error('BabylistAPI can only load in the browser'))
        if (window.bl && window.bl.addToRegistry) return Promise.resolve(window.bl)
        if (requestRef.current) return requestRef.current

        window._bl = {partner: siteKey}

        requestRef.current = new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.async = true
            script.src = 'https://www.babylist.com/add.js'
            script.onload = () => {
                if (window.bl && window.bl.addToRegistry) resolve(window.bl)
                else
                    setTimeout(() => {
                        window.bl && window.bl.addToRegistry
                            ? resolve(window.bl)
                            : reject(new Error('Failed to initialize Babylist'))
                    }, 30)
            }
            script.onerror = () => reject(new Error('Failed to load Babylist'))
            document.body.appendChild(script)
        })

        return requestRef.current
    }, [enabled, siteKey])

    const value = useMemo(() => ({enabled, apiLoader}), [enabled, apiLoader])
    return <BabylistContext.Provider value={value}>{children}</BabylistContext.Provider>
}

BabylistAPIProvider.propTypes = {
    children: PropTypes.any,
    enabled: PropTypes.bool,
    siteKey: PropTypes.string
}

export const useAddGiftToBabylist = (product, babylistBtnRef) => {
    const {buildUrl} = useMultiSite()
    const {enabled, apiLoader} = useBabylistAPILoader()
    const {locale} = useIntl()
    const {isCustomNetPriceEnabled} = useCurrentCustomer().data
    const mutationObserverRef = useRef(null)

    const isProdSet = isProductSet(product)

    const variationValues = React.useMemo(() => {
        return isProdSet ? {} : product.variationValues
    }, [isProdSet, product.variationValues])

    useEffect(() => {
        if (enabled) {
            mutationObserverRef.current = new MutationObserver((mutations) => {
                mutations.forEach((m) => {
                    m.addedNodes.forEach((node) => {
                        if (node.tagName === 'IFRAME' && node.src.includes('babylist.com')) {
                            node.style.opacity = '0'
                            node.style.transition = 'opacity 180ms ease'

                            node.addEventListener(
                                'load',
                                () => {
                                    requestAnimationFrame(() => {
                                        node.style.opacity = '1'
                                    })
                                },
                                {once: true}
                            )
                            babylistBtnRef?.current?.blur()
                            node.setAttribute('tabindex', '-1')
                            node.focus()
                        }
                    })

                    m.removedNodes.forEach((node) => {
                        if (node.tagName === 'IFRAME' && node.src.includes('babylist.com')) {
                            babylistBtnRef?.current?.focus()
                            mutationObserverRef.current.disconnect()
                        }
                    })
                })
            })
        }

        return () => mutationObserverRef.current?.disconnect()
    }, [enabled, babylistBtnRef])

    const imageLimit = isProdSet ? undefined : 1
    const baseImages = useProductDisplayImages(
        product,
        PRODUCT_TILE_IMAGE_TYPE,
        variationValues,
        imageLimit
    )

    const imageUrl = React.useMemo(() => {
        // single product
        if (!isProdSet) {
            return baseImages?.[0]?.link || ''
        }

        // product set
        try {
            const selectionImageName = getProductSetSelectionImageName(product)

            // Filters the baseImages array to find the one matching the string
            const combinedImages = filterSetCombinationImages(
                product,
                baseImages,
                selectionImageName
            )

            // Return the matched image, or fallback to the first available image if none match
            return combinedImages?.[0]?.link || baseImages?.[0]?.link || ''
        } catch (e) {
            throw new Error('Error resolving Product Set image for Babylist:', e.message)
        }
    }, [isProdSet, baseImages, product])

    const gift = React.useMemo(() => {
        if (!enabled || !product) return null

        const {displayPrice} = getDisplayPrice(product, false, isCustomNetPriceEnabled)

        const relativePath = isProdSet
            ? productSetUrlBuilder(product, locale)
            : productUrlBuilder(product, locale)

        let url = ''
        if (typeof window !== 'undefined') {
            const localizedPath = buildUrl(relativePath)
            url = new URL(localizedPath, window.location.origin).toString()
        }

        return {
            title: product?.variant?.name || product.name,
            image: imageUrl,
            url,
            price: displayPrice
        }
    }, [enabled, product, isCustomNetPriceEnabled, locale, buildUrl, imageUrl, isProdSet])

    const mutation = useMutation({
        mutationFn: async (giftPayload) => {
            if (!giftPayload) throw new Error('Gift payload is empty')

            const api = await apiLoader()

            api.addToRegistry({
                title: giftPayload.title,
                price: giftPayload.price,
                image: giftPayload.image,
                url: giftPayload.url
            })

            return giftPayload
        }
    })

    useEffect(() => {
        if (mutation.error) {
            mutationObserverRef.current?.disconnect()
        }
    }, [mutation.error])

    return {
        enabled,
        execute: () => {
            mutationObserverRef.current?.disconnect()
            mutationObserverRef.current?.observe(document.body, {childList: true, subtree: true})
            trackAddToBabylistClick(product)
            return mutation.mutateAsync(gift)
        },
        loading: mutation.isLoading,
        error: mutation.error
    }
}
